/**
 * 65.35 (ARCHITECTURE §5.21): an explosion class's blast at this scale — the one conversion of the person family's numbers
 * (constants/explosions.ts) to world units, which the LAW and the 40 mm (core/weapons.ts), the grenade, the strike drone
 * and the airstrike each did on their own: the convention radius R = (radiusPeople × a person's height × k) ÷ 2.2 (its
 * damage circle 2.2 R, `radiusPeople` person heights), in that expression order, so the floats are today's. Pure.
 */
import type { GameState, Bullet, WeaponKey } from '../types';
import { EXPLOSIONS, BLAST_RADII, BLAST_HEART, BLAST_WOUND_START, BLAST_BODY, SHAPED_CONTACT_PEOPLE, SHAPED_BLAST, type ExplosionClass } from '../constants/explosions';
import type { BurstModel } from './damage';
import { PERSON_BODY_HEIGHT, personSetOf, type PersonSet } from './scaleProfile';
import { defaultOn } from './defaultOn';

/** A person-family class's blast in world units for a person set (null: the class has no burst). */
export function explosionBlastFor(ps: Pick<PersonSet, 'k'>, cls: ExplosionClass): { radius: number; damage: number } | null {
  const b = EXPLOSIONS[cls].blast;
  return b ? { radius: (b.radiusPeople * PERSON_BODY_HEIGHT * ps.k) / 2.2, damage: b.damage } : null;
}

/** 69.65 (DECISIONS 289): the burst model is on — on the action clock unless the Lab's row turns it off (the turn game: only when asked). */
export function blastModelOn(config: Pick<GameState['config'], 'blastModel' | 'clock'> | undefined): boolean {
  return config?.blastModel === true || (config?.clock === 'action' && config.blastModel !== false);
}

/**
 * 69.65: a class's burst as one model in world units in this match (constants/explosions.ts BLAST_RADII), or null — the
 * switch off, or a class with no radii (the circle stays). A caller's own blast (`blast`, beside the class's: the mortar's
 * bomb, a load-widened wreck) scales the radii by the root of its circle's ratio times its damage's.
 */
export function burstModelFor(state: Pick<GameState, 'config'>, cls: ExplosionClass | undefined, blast?: { radius: number; damage: number }): BurstModel | null {
  const r = cls && blastModelOn(state.config) ? BLAST_RADII[cls] : undefined;
  if (!r) return null;
  const person = PERSON_BODY_HEIGHT * personSetOf(state).k, own = explosionBlast(state, cls!);
  const scale = blast && own ? Math.sqrt((blast.radius / own.radius) * (blast.damage / own.damage)) : 1;
  const at = person * scale;
  return {
    kill: r.kill * at, wound: r.wound * at, heart: r.heart ?? BLAST_HEART, woundStart: Math.min(BLAST_WOUND_START, r.heart ?? BLAST_WOUND_START), kind: r.kind,
    contact: SHAPED_CONTACT_PEOPLE * person, blastPen: SHAPED_BLAST.pen, blastShare: SHAPED_BLAST.share,
    body: { radius: BLAST_BODY.radius * person, stand: BLAST_BODY.stand * person, crouch: BLAST_BODY.crouch * person, prone: BLAST_BODY.prone * person },
  };
}

/**
 * §5.21 step 6: a class's push in world units in this match — its reach and its strength at the heart — with the Lab's Bursts
 * throw on (GameConfig.burstPush) and a push in its class; else undefined (today's push).
 */
export function explosionPush(state: Pick<GameState, 'config'>, cls: ExplosionClass | undefined): { radius: number; strength: number } | undefined {
  const p = cls && state.config.burstPush ? EXPLOSIONS[cls].push : undefined;
  return p ? { radius: p.reachPeople * PERSON_BODY_HEIGHT * personSetOf(state).k, strength: p.strength } : undefined;
}

/** A person-family class's blast in world units in this match (its person set's scale). */
export function explosionBlast(state: Pick<GameState, 'config'>, cls: ExplosionClass): { radius: number; damage: number } | null {
  return explosionBlastFor(personSetOf(state), cls);
}

/**
 * The class of a round that bursts (a bullet with a blast): the 40 mm's arms after `armAge` (only its round carries one),
 * else the LAW's. Read off the round itself, so the state carries nothing new (the per-tick checksum hashes every field).
 */
export function bulletExplosion(b: Pick<Bullet, 'armAge' | 'explosion'>): ExplosionClass {
  return b.explosion ?? (b.armAge !== undefined ? 'gl40' : 'law');   // (65.43: a newer round names its own — the Panzerfaust's, which arms too)
}

/** The class of a gun's shell by its weapon (the standard and the heavy; any other: null — the turn game's own). */
export function shellExplosion(weaponKey: WeaponKey | string): ExplosionClass | null {
  return weaponKey === 'standard' ? 'shell' : weaponKey === 'heavy' ? 'heavyShell' : null;
}

/**
 * §5.21 step 5: the owner of a wrecked vehicle's burst — no unit (a structure keeps no killer): below every unit id and apart
 * from an airstrike's (`-1 - team`), so its kills credit no one.
 */
export const vehicleBlastOwner = (structureId: number): number => -1000 - structureId;

/**
 * §5.21 step 7: a class's suppression in world units in this match — its reach and its mark's seconds — with Suppression
 * (65.28) and the Lab's Bursts suppress on and a suppression in its class; else undefined (bursts mark no one).
 */
export function explosionSuppress(state: Pick<GameState, 'config'>, cls: ExplosionClass | undefined): { radius: number; seconds: number } | undefined {
  const s = cls && defaultOn(state.config, 'suppression') && state.config.burstSuppress ? EXPLOSIONS[cls].suppress : undefined;
  return s ? { radius: s.reachPeople * PERSON_BODY_HEIGHT * personSetOf(state).k, seconds: s.seconds } : undefined;
}

/** The side a burst's owner is on: its unit's, an airstrike's (`-1 - team`), or none (a wreck's: everyone near is its enemy). */
export function blastOwnerTeam(state: Pick<GameState, 'players'>, ownerId: number): number | undefined {
  const u = state.players.find(p => p.id === ownerId);
  if (u) return u.teamIndex;
  return ownerId < 0 && ownerId > -1000 ? -1 - ownerId : undefined;
}
